import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  punchApi, usersApi, type PunchItem, type PunchEvent, type PunchListResponse, type PunchAction,
  type PunchPriority, type PunchAssigneeInput,
} from '@/lib/apiClient';
import { GraphConsentError, resolveDownloadUrl } from '@/lib/graphUpload';
import { isNotFoundError } from '@/lib/httpErrors';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { randomId } from '@/lib/randomId';
import { Button } from '@/components';
import {
  STATUS_LABELS, itemActions, passTargets, locationLabel, shortDate, itemFingerprint, pickableTrades, tradeName, stamp, cap,
  videoStepLabel, type ItemAction,
} from './punchModel';
import { StatusBadge, BicChip, Stepper, PhotoPicker, OutdoorToggle, VideoPicker, videoBlocks, type PickedVideo } from './punchUi';
import { uploadPunchVideo, attachClip, fileSize } from './punchVideos';
import { usePhoneLayout } from './usePhoneLayout';
import { uploadPunchPhoto, SIGN_IN_FAILED } from './punchPhotos';

type Data = PunchListResponse;

/** What each history step reads as. Unknown actions fall back to the raw name. */
const EVENT_LABELS: Record<string, string> = {
  created: 'Item added',
  sent: 'Sent',
  marked_fixed: 'Marked fixed',
  accepted: 'Accepted',
  rejected: 'Rejected',
  approval_recorded: 'Architect/owner approval recorded',
  reopened: 'Reopened',
  passed: 'Ball passed',
  edited: 'Edited',
  not_carried_over: 'Closed — not carried over',
  photo_added: 'Photo added',
  photo_removed: 'Photo removed',
  pinned: 'Pinned on a drawing',
  pin_moved: 'Pin moved',
  unpinned: 'Pin taken off the drawing',
  removed: 'Removed',
};

export default function PunchItemDrawer({
  projectId, itemId, data, canWork, canManage, jobNumber, jobFolderUrl, onClose, onChanged, onDrawing,
}: {
  projectId: number;
  itemId: number;
  data: Data;
  canWork: boolean;
  canManage: boolean;
  jobNumber: string;
  jobFolderUrl: string | null;
  onClose: () => void;
  onChanged: () => void;
  /** Open the drawings on this item's pin, or to place it: the viewer on a computer, the drawing screen on a phone. */
  onDrawing?: (itemId: number, place: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });
  const q = useQuery({
    queryKey: ['punch', projectId, 'item', itemId],
    queryFn: () => punchApi.item(projectId, itemId),
  });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const graph = useGraphToken();
  const phone = usePhoneLayout();
  const mediaUrls = useMemo(() => (q.data?.media ?? []).map((m) => m.webUrl), [q.data]);
  const { thumbs, reset } = useThumbnailCache(mediaUrls, graph.silent);

  // One tag per picked file, so a retried upload reuses its file name.
  // WeakMap, not Map: keyed by the File, so a photo or clip let go of is
  // freed rather than held while the item stays open — a clip can be
  // hundreds of MB (Codex on #593).
  const tagsRef = useRef(new WeakMap<File, string>());
  const photoTag = (file: File) => {
    let t = tagsRef.current.get(file);
    if (!t) { t = randomId().slice(0, 8); tagsRef.current.set(file, t); }
    return t;
  };
  const [panel, setPanel] = useState<'reject' | 'after_photo' | 'pass_to' | 'edit' | 'add_photo' | 'add_video' | null>(null);
  // A clip's upload, 0–1, while one is going up.
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const qc = useQueryClient();
  const list = data.list!;
  const ao = list.requiresAoApproval;

  /**
   * Sign in to SharePoint straight from the tap, before any upload work
   * (popup blockers). Any failure — popup blocked or closed, no consent —
   * reads as SIGN_IN_FAILED, not MSAL's raw error text.
   */
  const signIn = async () => {
    try {
      await graph.interactive();
    } catch {
      throw new GraphConsentError('SharePoint sign-in failed');
    }
  };

  /** Close first, then drop the item's cached detail: refetching it would only 404. */
  const removeItem = async () => {
    setBusy(true);
    setError(null);
    const gone = () => {
      onClose();
      qc.removeQueries({ queryKey: ['punch', projectId, 'item', itemId] });
      onChanged();
    };
    try {
      await punchApi.removeItem(projectId, itemId);
      gone();
    } catch (e) {
      // A removal whose reply was lost: the retry answers 404, and a re-read
      // does too — the item is gone, which is what was asked (Codex on #565).
      const stillThere = isNotFoundError(e)
        ? false
        : await punchApi.item(projectId, itemId).then(() => true, (re) => !isNotFoundError(re));
      if (!stillThere) { gone(); return; }
      setError((e as Error)?.message ?? 'The item was not removed.');
      setBusy(false);
    }
  };

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    const before = q.data ? itemFingerprint(q.data) : null;
    try {
      await fn();
      setPanel(null);
      onChanged();
      await q.refetch();
      after?.();
    } catch (e) {
      // The step may have gone through with only its reply lost — a retry of
      // it is then refused (e.g. mark fixed on an item already Ready for
      // review). Reload before saying it failed (Codex on #565). If the item
      // moved since the tap, show its current state with a neutral note, not
      // a false "failed": it was either this step, or someone else's at the
      // same moment — the history says which.
      onChanged();
      const now = await q.refetch().then((r) => r.data).catch(() => undefined);
      if (e instanceof PartialStepError) {
        setPanel(null);
        // The accept may have gone through with only its reply lost (or only
        // the activity log after it failed): if the item has moved past Ready
        // for review, it was verified — say nothing went wrong (Codex on #569).
        const verified = now && (now.status === 'verified' || now.status === 'closed');
        if (!verified) setError(e.message);
      } else if (before && now && itemFingerprint(now) !== before && !(e instanceof GraphConsentError)) {
        setPanel(null);
        setNotice('This item changed while that was being saved — this is its current state. Check the history to see whether your step went through.');
      } else {
        setError(e instanceof GraphConsentError ? SIGN_IN_FAILED : (e as Error)?.message ?? 'That didn’t work.');
      }
    } finally {
      setBusy(false);
    }
  };

  const act = (action: PunchAction, extra: { reason?: string; note?: string; bicTo?: 'ew' | 'assignee' | 'ao'; bicUserId?: string } = {}) =>
    run(() => punchApi.act(projectId, itemId, { action, ...extra }));

  const item = q.data;
  const access = { canWork, canManage };

  // Photos and videos: the uploader may remove their own, a manager any — but
  // not a mark-fixed step's evidence, the last before photo, or anything on a
  // closed-out list, whose photos are its record (the API refuses those).
  const canRemoveMedia = (m: PunchItem['media'][number]) => !!item && canWork && list.stage !== 'closed_out' && !m.fixEvidence
    && (canManage || (!!me && m.uploadedBy?.toLowerCase() === me.id.toLowerCase()))
    && !(m.kind === 'before' && item.media.filter((x) => x.kind === 'before').length <= 1);
  const removeMedia = (mediaId: number) => {
    const what = item?.media.find((m) => m.id === mediaId)?.kind === 'video' ? 'video' : 'photo';
    if (!window.confirm(`Remove this ${what} from the item? The file stays in the Job Folder.`)) return;
    void run(() => punchApi.removeMedia(projectId, itemId, mediaId));
  };

  return (
    <>
      <div className="pl-scrim" onClick={onClose} />
      <aside ref={ref} className="pl-drawer" role="dialog" aria-modal="true" aria-label={item ? `Punch item ${item.label}` : 'Punch item'}>
        <div className="pl-drawer-hd">
          {item && <span className="pl-drawer-no">{item.label}</span>}
          {item && <StatusBadge status={item.status} />}
          {item && <span className="pl-muted">{item.origin === 'internal' ? 'Internal item' : 'Published item'}</span>}
          <span className="pl-spacer" />
          {phone && <OutdoorToggle />}
          <button type="button" className="pl-link" onClick={onClose} aria-label="Close">Close ✕</button>
        </div>

        <div className="pl-drawer-bd">
          {q.isLoading && <p className="pl-muted">Loading…</p>}
          {q.isError && (
            <div className="pl-error">
              {isNotFoundError(q.error)
                ? 'This item is no longer on the list — it may have been removed.'
                : (q.error as Error).message}
            </div>
          )}
          {item && (
            <>
              <div>
                <h3 className="pl-item-title">{item.description}</h3>
                <div className="pl-item-where">
                  {locationLabel(item)} · {tradeName(data.trades, item.trade)}
                </div>
              </div>

              {item.status === 'rejected' && item.rejectReason && (
                <div className="pl-error">
                  Rejected: {data.rejectReasons.find((r) => r.key === item.rejectReason)?.label ?? item.rejectReason}
                </div>
              )}

              <div className="pl-panel">
                <div className="pl-caption" style={{ marginBottom: 8 }}>Ball in court</div>
                <div className="pl-row">
                  <BicChip type={item.bic.type} label={item.bic.label} large />
                  <span className="pl-muted">{ballHint(item)}</span>
                  <span className="pl-spacer" />
                  {canWork && item.status !== 'closed' && (
                    <Button variant="secondary" size="sm" onClick={() => setPanel('pass_to')}>Pass To…</Button>
                  )}
                </div>
              </div>

              {panel === 'pass_to' && (
                <PassToPanel item={item} ao={ao} meId={me?.id ?? null} busy={busy} onCancel={() => setPanel(null)}
                  onPass={(bicTo, bicUserId) => act('pass_to', { bicTo, bicUserId })} />
              )}

              <Photos
                item={item}
                busy={busy}
                thumbs={thumbs}
                canRemove={canRemoveMedia}
                onRemove={removeMedia}
                needsSignIn={graph.needsSignIn}
                onSignIn={async () => { try { await graph.interactive(); reset(); } catch { setError(SIGN_IN_FAILED); } }}
              />
              <Videos item={item} busy={busy} thumbs={thumbs} canRemove={canRemoveMedia} onRemove={removeMedia}
                getToken={graph.interactive} />
              {canWork && item.status !== 'closed' && jobFolderUrl && panel !== 'add_photo' && panel !== 'add_video' && (
                <div className="pl-row">
                  <button type="button" className="pl-link" onClick={() => setPanel('add_photo')}>+ Add a photo</button>
                  <button type="button" className="pl-link" onClick={() => setPanel('add_video')}>+ Add a video</button>
                </div>
              )}

              {/* Pins on drawings (#518 phase 3): on a phone too, which opens the phone's drawing screen. */}
              {onDrawing && (item.pin || (canWork && item.status !== 'closed' && list.stage !== 'closed_out')) && (
                <div className="pl-row">
                  <span className="pl-muted">{item.pin ? `📍 On a drawing, page ${item.pin.page}` : 'Not on a drawing'}</span>
                  {item.pin && <button type="button" className="pl-link" onClick={() => onDrawing(item.id, false)}>View on Drawing</button>}
                  {canWork && item.status !== 'closed' && list.stage !== 'closed_out' && (
                    <button type="button" className="pl-link" onClick={() => onDrawing(item.id, true)}>
                      {item.pin ? 'Move Pin' : 'Place on Drawing'}
                    </button>
                  )}
                </div>
              )}

              <div>
                <div className="pl-caption" style={{ marginBottom: 8 }}>Status</div>
                <Stepper className="pl-stepper pl-steps" steps={statusSteps(item.status, ao)} current={statusIndex(item.status, ao)} />
              </div>

              <div className="pl-facts">
                <Fact label="Assigned to" value={item.assigneeLabel ?? 'Unassigned'} />
                <Fact label="Trade" value={tradeName(data.trades, item.trade)} />
                <Fact label="Location" value={locationLabel(item)} />
                <Fact label="Due · Priority" value={`${shortDate(item.dueDate) || 'No due date'} · ${cap(item.priority)}`}
                  danger={item.overdue} />
                <Fact label="Field leader" value={item.fieldLeader?.name ?? 'None'} />
                <Fact label="Last updated"
                  value={[stamp(item.lastUpdatedAt), item.lastUpdatedBy].filter(Boolean).join(' · ') || '—'} />
              </div>
              {item.notes && (
                <div>
                  <div className="pl-caption" style={{ marginBottom: 6 }}>Notes</div>
                  <div className="pl-notes">{item.notes}</div>
                </div>
              )}
              {canWork && item.status !== 'closed' && panel !== 'edit' && (
                <div><button type="button" className="pl-link" onClick={() => setPanel('edit')}>Edit details</button></div>
              )}

              {panel === 'edit' && (
                <EditPanel projectId={projectId} item={item} data={data} busy={busy}
                  onCancel={() => setPanel(null)}
                  onSave={(patch) => run(() => punchApi.updateItem(projectId, itemId, patch))} />
              )}
              {panel === 'reject' && (
                <RejectPanel data={data} assignee={item.assigneeLabel} busy={busy}
                  onCancel={() => setPanel(null)}
                  onReject={(reason, note) => act('reject', { reason, note: note || undefined })} />
              )}
              {panel === 'after_photo' && (
                <PhotoPanel title={`After photo · ${item.label}`} hint="Required to mark fixed. Line it up with the before photo."
                  submitLabel="Upload & Send for Review" busy={busy} disabled={!jobFolderUrl}
                  onCancel={() => setPanel(null)}
                  // "I checked it" (design, Mark fixed on a phone): the field
                  // leader on site checks the fix as they photograph it, so it
                  // is marked fixed and then accepted — two steps, each its
                  // own history entry. Accept is field-level, and marking
                  // fixed leaves the ball with E&W, as accepting needs.
                  verifyLabel={ao ? 'I Checked It — Verify Now' : 'I Checked It — Verify & Close'}
                  onSubmit={(file, verifyNow) => run(async () => {
                    await signIn();
                    const { photo } = await uploadPunchPhoto({
                      file, kind: 'after', itemNo: item.itemNo, jobNumber, jobFolderUrl: jobFolderUrl!,
                      subfolder: data.photoSubfolder, getToken: graph.interactive, tag: photoTag(file),
                    });
                    try {
                      await punchApi.act(projectId, itemId, { action: 'mark_fixed', afterPhoto: photo });
                    } catch (e) {
                      // Mark fixed may have gone through with only its reply
                      // lost (or only the activity log after it failed). With
                      // Verify Now asked for, carry on when the item is now
                      // Ready for review with E&W, rather than stop half-way
                      // (Codex on #569); anything else is reported as before.
                      if (!verifyNow) throw e;
                      const now = await punchApi.item(projectId, itemId).catch(() => null);
                      // And it must be THIS fix: another person marking the item
                      // fixed first also leaves it Ready for review with E&W, and
                      // accepting that would pass their repair on a photo of ours
                      // that never went on (Codex on #569).
                      const ours = now?.media.some((m) => m.kind === 'after' && m.webUrl === photo.webUrl);
                      if (!(now?.status === 'ready_for_review' && now.bic.type === 'ew' && ours)) throw e;
                    }
                    if (!verifyNow) return;
                    try {
                      await punchApi.act(projectId, itemId, { action: 'accept' });
                    } catch (e) {
                      // The fix is recorded; only the verify didn't go. Say so,
                      // not "this item changed while saving".
                      throw new PartialStepError(`Marked fixed, but not verified: ${(e as Error)?.message ?? 'try again'}. Accept it from this item when you can.`);
                    }
                  })} />
              )}
              {panel === 'add_video' && (
                <AddVideoPanel busy={busy} progress={progress} onCancel={() => setPanel(null)}
                  onSubmit={({ file, recorded }) => run(async () => {
                    await signIn();
                    setProgress(0);
                    try {
                      const clip = await uploadPunchVideo({
                        file, recorded, itemNo: item.itemNo, jobNumber, jobFolderUrl: jobFolderUrl!, subfolder: data.photoSubfolder,
                        getToken: graph.interactive, tag: photoTag(file), onProgress: (sent, total) => setProgress(total ? sent / total : 0),
                      });
                      // One more try after a dropped reply, so a blip doesn't mean uploading the clip again.
                      return await attachClip(() => punchApi.addMedia(projectId, itemId, 'video', clip));
                    } finally {
                      setProgress(null);
                    }
                  })} />
              )}
              {panel === 'add_photo' && (
                <AddPhotoPanel busy={busy} onCancel={() => setPanel(null)}
                  onSubmit={(file, kind) => run(async () => {
                    await signIn();
                    const { photo } = await uploadPunchPhoto({
                      file, kind, itemNo: item.itemNo, jobNumber, jobFolderUrl: jobFolderUrl!,
                      subfolder: data.photoSubfolder, getToken: graph.interactive, tag: photoTag(file),
                    });
                    return punchApi.addMedia(projectId, itemId, kind, photo);
                  })} />
              )}

              {notice && <div className="pl-note" role="status">{notice}</div>}
              {error && <div className="pl-error">{error}</div>}

              <History events={item.events} reasons={data.rejectReasons} />
            </>
          )}
        </div>

        {item && (canWork || canManage) && (
          <div className="pl-drawer-ft">
            {canManage && item.status !== 'closed' && (
              <Button variant="secondary" disabled={busy} onClick={() => {
                if (!window.confirm(`Remove ${item.label}? Use this only for an item added by mistake.`)) return;
                void removeItem();
              }}>Remove</Button>
            )}
            <span className="pl-spacer" />
            {itemActions(item, access, ao).map((a) => (
              <ActionButton key={a.label} a={a} busy={busy} disabled={a.opens === 'after_photo' && !jobFolderUrl}
                onClick={() => (a.opens ? setPanel(a.opens) : act(a.action as PunchAction))} />
            ))}
          </div>
        )}
      </aside>

    </>
  );
}

/** A two-step action whose first step went through and whose second didn't. */
class PartialStepError extends Error {}

function ActionButton({ a, busy, disabled, onClick }: { a: ItemAction; busy: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant={a.variant === 'danger' ? 'danger' : a.variant} disabled={busy || disabled} onClick={onClick}>
      {a.label}
    </Button>
  );
}

function ballHint(item: PunchItem): string {
  switch (item.status) {
    case 'new': return 'to send';
    case 'open':
    case 'rejected': return item.bic.type === 'assignee' ? 'to fix' : '';
    case 'ready_for_review': return item.bic.type === 'ew' ? 'to verify' : '';
    case 'verified': return item.bic.type === 'ao' ? 'to approve' : '';
    default: return '';
  }
}

function statusSteps(status: PunchItem['status'], ao: boolean): string[] {
  const first = status === 'new' ? 'New' : status === 'rejected' ? 'Rejected' : 'Open';
  return ao ? [first, 'Ready for review', 'Verified', 'Closed'] : [first, 'Ready for review', 'Closed'];
}

function statusIndex(status: PunchItem['status'], ao: boolean): number {
  const order = ao ? ['open', 'ready_for_review', 'verified', 'closed'] : ['open', 'ready_for_review', 'closed'];
  const s = status === 'new' || status === 'rejected' ? 'open' : status === 'verified' && !ao ? 'ready_for_review' : status;
  return Math.max(0, order.indexOf(s));
}



function Fact({ label, value, danger = false }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <div className="pl-caption">{label}</div>
      <div className={`pl-fact-v${danger ? ' pl-overdue' : ''}`}>{value}</div>
    </div>
  );
}

function Photos({ item, busy, thumbs, canRemove, onRemove, needsSignIn, onSignIn }: {
  item: PunchItem;
  busy: boolean;
  thumbs: Record<string, string>;
  canRemove: (m: PunchItem['media'][number]) => boolean;
  onRemove: (mediaId: number) => void;
  needsSignIn: boolean;
  onSignIn: () => void;
}) {
  const before = item.media.filter((m) => m.kind === 'before');
  const after = item.media.filter((m) => m.kind === 'after');
  const tile = (m: PunchItem['media'][number] | undefined, kind: 'before' | 'after') => {
    if (!m) {
      return (
        <div className={`pl-photo empty`}>
          {kind === 'after' ? 'After photo — required to mark fixed' : 'No before photo'}
        </div>
      );
    }
    const src = thumbs[m.webUrl];
    return (
      <div className={`pl-photo ${kind}`}>
        {/* The full-size original in SharePoint: the evidence a fix is accepted or rejected on. */}
        <a className="pl-photo-link" href={m.webUrl} target="_blank" rel="noreferrer" title="Open the full-size photo">
          {src ? <img src={src} alt={`${kind} photo`} /> : <span>{m.fileName}</span>}
        </a>
        <span className="pl-photo-tag">
          {kind === 'before' ? 'Before' : 'After'}{m.takenAt ? ` · ${shortDate(m.takenAt)}` : ''}
        </span>
        {canRemove(m) && (
          <button type="button" className="pl-photo-x" disabled={busy} onClick={() => onRemove(m.id)} aria-label={`Remove ${kind} photo`}>Remove</button>
        )}
      </div>
    );
  };
  const extra = [...before.slice(1), ...after.slice(0, -1)];
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div className="pl-photos">
        {tile(before[0], 'before')}
        {tile(after[after.length - 1], 'after')}
      </div>
      {(extra.length > 0 || needsSignIn) && (
        <div className="pl-strip">
          {extra.map((m) => (
            <span key={m.id} className="pl-row" style={{ gap: 4 }}>
              <a href={m.webUrl} target="_blank" rel="noreferrer">
                {m.kind === 'before' ? 'Before' : 'Earlier after'} photo{m.takenAt ? ` · ${shortDate(m.takenAt)}` : ''}
                {m.fixEvidence ? ' (evidence)' : ''}
              </a>
              {canRemove(m) && (
                <button type="button" className="pl-link" disabled={busy} onClick={() => onRemove(m.id)}
                  aria-label={`Remove ${m.kind} photo ${m.fileName}`}>Remove</button>
              )}
            </span>
          ))}
          {needsSignIn && item.media.length > 0 && (
            <button type="button" className="pl-link" onClick={onSignIn}>Show previews</button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The item's clips (#518 phase 2). Each plays here, from a short-lived
 * download link fetched when Play is tapped (the SharePoint page isn't a
 * file a <video> can play); the SharePoint page is always one tap away, and
 * the fallback when the link can't be had or the browser can't play it.
 */
export function Videos({ item, busy, thumbs, canRemove, onRemove, getToken }: {
  item: PunchItem;
  busy: boolean;
  thumbs: Record<string, string>;
  canRemove: (m: PunchItem['media'][number]) => boolean;
  onRemove: (mediaId: number) => void;
  getToken: () => Promise<string>;
}) {
  const videos = item.media.filter((m) => m.kind === 'video');
  if (videos.length === 0) return null;
  return (
    <section aria-label="Videos">
      <div className="pl-caption" style={{ marginBottom: 8 }}>{videos.length === 1 ? 'Video' : `Videos (${videos.length})`}</div>
      <div className="pl-videos">
        {videos.map((m) => (
          <VideoTile key={m.id} m={m} poster={thumbs[m.webUrl]} busy={busy} canRemove={canRemove(m)}
            onRemove={() => onRemove(m.id)} getToken={getToken} />
        ))}
      </div>
    </section>
  );
}

function VideoTile({ m, poster, busy, canRemove, onRemove, getToken }: {
  m: PunchItem['media'][number]; poster: string | undefined; busy: boolean; canRemove: boolean;
  onRemove: () => void; getToken: () => Promise<string>;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const when = m.takenAt ? shortDate(m.takenAt) : '';
  const caption = ['Video', when || `added ${shortDate(m.uploadedAt)}`, fileSize(m.sizeBytes)].filter(Boolean).join(' · ');
  const play = async () => {
    setState('loading');
    const url = await resolveDownloadUrl(m.webUrl, getToken);
    if (url) { setSrc(url); setState('idle'); } else setState('failed');
  };
  return (
    <div className="pl-video">
      {src ? (
        // The link is short-lived: a play that fails later (it expired, or the
        // browser can't play this format) falls back to the SharePoint page.
        <video className="pl-video-player" src={src} poster={poster} controls autoPlay playsInline
          onError={() => { setSrc(null); setState('failed'); }} />
      ) : (
        <button type="button" className="pl-video-poster" disabled={state === 'loading'} onClick={() => { void play(); }}
          aria-label={`Play video${when ? ` from ${when}` : ''}`}>
          {poster && <img src={poster} alt="" />}
          <span className="pl-video-play" aria-hidden>{state === 'loading' ? '…' : '▶'}</span>
        </button>
      )}
      <div className="pl-row pl-video-meta">
        <span>{caption}</span>
        <span className="pl-spacer" />
        {/* pl-link: 44px tall on a phone, like the drawer's other links — it is the fallback when a clip won't play. */}
        <a className="pl-link" href={m.webUrl} target="_blank" rel="noreferrer">Open in SharePoint</a>
        {canRemove && (
          <button type="button" className="pl-link" disabled={busy} onClick={onRemove} aria-label={`Remove video ${m.fileName}`}>Remove</button>
        )}
      </div>
      {state === 'failed' && <div className="pl-error">Couldn’t play it here. Open it in SharePoint instead.</div>}
    </div>
  );
}

function AddVideoPanel({ busy, progress, onCancel, onSubmit }: {
  busy: boolean; progress: number | null; onCancel: () => void; onSubmit: (picked: PickedVideo) => void;
}) {
  const [picked, setPicked] = useState<PickedVideo | null>(null);
  const pct = progress == null ? null : Math.round(progress * 100);
  return (
    <div className="pl-panel pl-form">
      <div className="pl-caption">Add a video</div>
      <VideoPicker label="Video" picked={picked} onChange={setPicked} busy={busy} />
      {pct != null && (
        <div className="pl-row">
          <div className="pl-bar" role="progressbar" aria-label="Uploading the video" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="pl-muted">Uploading… {pct}%</span>
        </div>
      )}
      <div className="pl-row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={!picked || videoBlocks(picked)} isLoading={busy} onClick={() => picked && onSubmit(picked)}>
          Add Video
        </Button>
      </div>
    </div>
  );
}

function History({ events, reasons }: { events: PunchEvent[]; reasons: PunchListResponse['rejectReasons'] }) {
  if (events.length === 0) return null;
  return (
    <section>
      <div className="pl-caption" style={{ marginBottom: 10 }}>History</div>
      <div className="pl-history">
        {events.map((e) => (
          <div key={e.id} className="pl-ev">
            <span className="pl-ev-dot" aria-hidden />
            <div>
              <div className="pl-ev-what">
                {videoStepLabel(e) ?? EVENT_LABELS[e.action] ?? e.action}
                {e.statusTo ? ` — ${STATUS_LABELS[e.statusTo]}` : ''}
              </div>
              <div className="pl-ev-meta">
                {[e.actor, new Date(e.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
                  e.bicTo ? `Ball → ${e.bicTo.label}` : null].filter(Boolean).join(' · ')}
              </div>
              {/* The picked reason, not just the optional note: once the item is fixed
                  again the "Rejected" banner is gone, and this is the only place a
                  past rejection's reason still shows (Codex on #565). */}
              {e.reason && (
                <div className="pl-ev-note">Reason: {reasons.find((r) => r.key === e.reason)?.label ?? e.reason}</div>
              )}
              {e.note && !videoStepLabel(e) && <div className="pl-ev-note">{e.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RejectPanel({ data, assignee, busy, onCancel, onReject }: {
  data: Data; assignee: string | null; busy: boolean; onCancel: () => void; onReject: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  return (
    <div className="pl-panel pl-form">
      <div className="pl-caption">Why is it going back?</div>
      <div className="pl-chips" role="group" aria-label="Reason">
        {data.rejectReasons.map((r) => (
          <button key={r.key} type="button" className="pl-chip" aria-pressed={reason === r.key} onClick={() => setReason(r.key)}>
            {r.label}
          </button>
        ))}
      </div>
      <label className="pl-field"><span>Note (optional)</span>
        <textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="pl-row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="danger" disabled={!reason} isLoading={busy} onClick={() => onReject(reason, note.trim())}>
          Send Back{assignee ? ` to ${assignee}` : ''}
        </Button>
      </div>
    </div>
  );
}

function PhotoPanel({ title, hint, submitLabel, verifyLabel, busy, disabled, onCancel, onSubmit }: {
  title: string; hint: string; submitLabel: string; verifyLabel: string; busy: boolean; disabled: boolean;
  onCancel: () => void; onSubmit: (file: File, verifyNow: boolean) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  return (
    <div className="pl-panel pl-form">
      <div className="pl-caption">{title}</div>
      <p className="pl-muted" style={{ margin: 0 }}>{hint}</p>
      <PhotoPicker label="After photo" file={file} onChange={setFile} required />
      {file && (
        <div className="pl-note" style={{ margin: 0 }}>
          Send for Review makes it Ready for review, with the ball at E&amp;W to verify.
        </div>
      )}
      <div className="pl-row pl-panel-actions" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="secondary" disabled={!file || disabled || busy} onClick={() => file && onSubmit(file, true)}>{verifyLabel}</Button>
        <Button variant="primary" disabled={!file || disabled} isLoading={busy} onClick={() => file && onSubmit(file, false)}>{submitLabel}</Button>
      </div>
    </div>
  );
}

function AddPhotoPanel({ busy, onCancel, onSubmit }: {
  busy: boolean; onCancel: () => void; onSubmit: (file: File, kind: 'before' | 'after') => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<'before' | 'after'>('before');
  return (
    <div className="pl-panel pl-form">
      <div className="pl-caption">Add a photo</div>
      <div className="pl-chips" role="group" aria-label="Photo kind">
        <button type="button" className="pl-chip" aria-pressed={kind === 'before'} onClick={() => setKind('before')}>Before</button>
        <button type="button" className="pl-chip" aria-pressed={kind === 'after'} onClick={() => setKind('after')}>After</button>
      </div>
      <PhotoPicker label="Photo" file={file} onChange={setFile} required />
      <div className="pl-row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={!file} isLoading={busy} onClick={() => file && onSubmit(file, kind)}>Add Photo</Button>
      </div>
    </div>
  );
}

function EditPanel({ projectId, item: live, data, busy, onCancel, onSave }: {
  projectId: number; item: PunchItem; data: Data; busy: boolean;
  onCancel: () => void; onSave: (patch: Parameters<typeof punchApi.updateItem>[2]) => void;
}) {
  const subsQ = useQuery({ queryKey: ['punch', projectId, 'subs'], queryFn: () => punchApi.subs(projectId), staleTime: 60_000 });
  // The item as it was when the form opened; see Save.
  const [item] = useState(live);
  const [description, setDescription] = useState(item.description);
  const [trade, setTrade] = useState(item.trade ?? '');
  const [assignee, setAssignee] = useState(assigneeValue(item.assignee));
  const [room, setRoom] = useState(item.room ?? '');
  const [notes, setNotes] = useState(item.notes ?? '');
  const [leader, setLeader] = useState(item.fieldLeader?.id ?? '');
  // The job's field leaders, and the item's current one even if they've since
  // moved off the job, so opening the form never silently changes it.
  const leaderOptions = useMemo(() => {
    const list = [...(subsQ.data?.fieldLeaders ?? [])];
    const cur = item.fieldLeader;
    if (cur?.id && !list.some((l) => l.id.toLowerCase() === cur.id!.toLowerCase())) list.unshift({ id: cur.id, name: cur.name });
    return list;
  }, [subsQ.data, item.fieldLeader]);
  const noCurrentLeaders = subsQ.isSuccess && (subsQ.data.fieldLeaders ?? []).length === 0;
  const [due, setDue] = useState(item.dueDate ?? '');
  const [priority, setPriority] = useState<PunchPriority>(item.priority);
  const sent = item.status !== 'new';
  // The current sub stays selectable even before the list loads (or if it has
  // since dropped off it), so opening the form never silently changes it.
  const subOptions = useMemo(() => {
    const names = (subsQ.data?.subs ?? []).map((x) => x.name);
    const current = item.assignee?.type === 'sub' ? item.assignee.name : null;
    if (current && !names.some((n) => n.toLowerCase() === current.toLowerCase())) names.unshift(current);
    return names;
  }, [subsQ.data, item.assignee]);
  return (
    <div className="pl-panel pl-form">
      <div className="pl-caption">Edit details</div>
      <label className="pl-field"><span>What needs fixing</span>
        <textarea value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="pl-grid2">
        <label className="pl-field"><span>Trade</span>
          <select value={trade} onChange={(e) => setTrade(e.target.value)}>
            {!item.trade && <option value="">—</option>}
            {pickableTrades(data.trades, item.trade).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        <label className="pl-field"><span>Assigned to</span>
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            {!sent && <option value="">Unassigned</option>}
            <option value="ew_crew">{data.ewCrewLabel}</option>
            {subOptions.map((name) => <option key={name} value={`sub:${name}`}>{name}</option>)}
          </select>
        </label>
        <label className="pl-field"><span>Room or area</span>
          <input value={room} maxLength={60} onChange={(e) => setRoom(e.target.value)} />
        </label>
        <label className="pl-field"><span>Field leader</span>
          <select value={leader} disabled={leaderOptions.length === 0} onChange={(e) => setLeader(e.target.value)}>
            {/* With nobody leading the job now, the saved leader can be cleared
                ("None"); otherwise a blank only shows until one is picked
                (Codex on #566). */}
            {(!leader || noCurrentLeaders) && (
              <option value="">{noCurrentLeaders ? 'None' : 'Pick the field leader…'}</option>
            )}
            {leaderOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <label className="pl-field"><span>Due</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <label className="pl-field"><span>Priority</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value as PunchPriority)}>
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
          </select>
        </label>
      </div>
      <label className="pl-field"><span>Notes</span>
        <textarea value={notes} maxLength={1000} placeholder="Which wall, access, a spec reference — anything else"
          onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="pl-row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" isLoading={busy} disabled={!description.trim()} onClick={() => {
          // Only what changed since the form opened: the item can refetch
          // while it is open, and resending untouched fields would undo
          // someone else's edit made meanwhile (Codex on #565).
          const patch: Parameters<typeof punchApi.updateItem>[2] = {};
          if (description.trim() !== item.description) patch.description = description.trim();
          if (trade && trade !== (item.trade ?? '')) patch.trade = trade;
          if (assignee !== assigneeValue(item.assignee)) patch.assignee = toAssignee(assignee);
          if ((room.trim() || null) !== item.room) patch.room = room.trim() || null;
          if ((notes.trim() || null) !== item.notes) patch.notes = notes.trim() || null;
          if (leader && leader.toLowerCase() !== (item.fieldLeader?.id ?? '').toLowerCase()) patch.fieldLeaderId = leader;
          if (!leader && item.fieldLeader && noCurrentLeaders) patch.fieldLeaderId = null;
          if ((due || null) !== item.dueDate) patch.dueDate = due || null;
          if (priority !== item.priority) patch.priority = priority;
          if (Object.keys(patch).length === 0) onCancel(); else onSave(patch);
        }}>Save</Button>
      </div>
    </div>
  );
}

function assigneeValue(a: PunchItem['assignee']): string {
  if (!a) return '';
  return a.type === 'ew_crew' ? 'ew_crew' : `sub:${a.name}`;
}

function toAssignee(v: string): PunchAssigneeInput | null {
  if (!v) return null;
  if (v === 'ew_crew') return { type: 'ew_crew' };
  return { type: 'sub', name: v.slice(4) };
}

/**
 * Where to pass the ball. An in-drawer panel rather than a modal: a modal
 * inside the drawer's focus trap fought it (Escape closed the drawer, and Tab
 * could not get past the first option).
 */
function PassToPanel({ item, ao, meId, busy, onCancel, onPass }: {
  item: PunchItem; ao: boolean; meId: string | null; busy: boolean;
  onCancel: () => void; onPass: (bicTo: 'ew' | 'assignee' | 'ao', bicUserId?: string) => void;
}) {
  const t = passTargets(item, ao);
  const alreadyMine = item.bic.type === 'ew' && !!meId && item.bic.userId?.toLowerCase() === meId.toLowerCase();
  const alreadyEw = item.bic.type === 'ew' && !item.bic.userId;
  return (
    <div className="pl-panel pl-form" role="group" aria-label={`Pass ${item.label} to`}>
        <div className="pl-caption">Pass {item.label} to — E&amp;W</div>
        <div className="pl-pick">
          <button type="button" className="pl-pick-row" disabled={busy || alreadyEw} onClick={() => onPass('ew')}>
            <span>E&amp;W</span><span className="pl-muted">no one named</span>
          </button>
          {meId && (
            <button type="button" className="pl-pick-row" disabled={busy || alreadyMine} onClick={() => onPass('ew', meId)}>
              <span>Me</span><span className="pl-muted">it’s my move</span>
            </button>
          )}
        </div>
        <div className="pl-caption">Subcontractor</div>
        <div className="pl-pick">
          <button type="button" className="pl-pick-row" disabled={busy || !t.assignee} onClick={() => onPass('assignee')}>
            <span>{item.assigneeLabel ?? 'No one assigned'}</span>
            {item.bic.type === 'assignee' && <span className="pl-muted">has it now</span>}
          </button>
        </div>
        <div className="pl-caption">Architect/Owner</div>
        {ao ? (
          <div className="pl-pick">
            <button type="button" className="pl-pick-row" disabled={busy || !t.ao} onClick={() => onPass('ao')}>
              <span>Architect/Owner</span>
              {item.bic.type === 'ao' && <span className="pl-muted">has it now</span>}
            </button>
          </div>
        ) : <p className="pl-muted" style={{ margin: 0 }}>Approval is off for this job.</p>}
        <div className="pl-row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
    </div>
  );
}
